import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { PhysicalInventoryShipmentInfo, PhysicalInventoryShipmentOrderInfo, IncludeType } from '../../../../modules/inventory/models/physical-inventory-shipments';
import { ShipmentTypeMetadata } from "../../../../models/enums/shipment-type";
import { ShipmentsUpdate } from '../../../../modules/inventory/models/shipments-update';
import { Table } from '../../../../components/tables/Table';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { formatCurrency } from "../../../../utils/currency-formatter";
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { PhysicalInventoryStatus } from '../../../../modules/inventory/models/physical-inventory-enums';
import { ShipmentStatusMetadata } from '../../../../transportation/models/enums/shipment-status';
import { ShipmentType } from '../../../../models/enums/shipment-type';

interface IProps {
    location: any;
}

export const InventoryPhysicalShipmentsScene = React.forwardRef((props: IProps, ref) => {

    const [service] = React.useState(new InventorySvc());
    const [includedTrucks, setIncludedTrucks] = React.useState(0);
    const [includedContainers, setIncludedContainers] = React.useState(0);
    const [totalValue, setTotalValue] = React.useState<number | undefined>(undefined);
    const [shipments, setShipments] = React.useState(new Array<PhysicalInventoryShipmentInfo>());
    const [reportStatus, setReportStatus] = React.useState(PhysicalInventoryStatus.inProgress);

    // used for orders view
    const [displayedShipment, setDisplayedShipment] = React.useState<PhysicalInventoryShipmentInfo | null>(null);
    const [displayedOrders, setDisplayedOrders] = React.useState(new Array<PhysicalInventoryShipmentOrderInfo>());
    const [includedOrderCount, setIncludedOrderCount] = React.useState(0);

    // initialization 
    React.useEffect(() => {
        getPageData(getReportId());
    }, [props.location]);

    React.useImperativeHandle(ref, () => ({
        goBack() {
            goBack();
        }
    }))

    function disableEdit(): boolean {
        return reportStatus == PhysicalInventoryStatus.complete;
    }

    // global
    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    function getButtons() {
        if (displayedShipment) {
            return <HasPermission requiredPermissions={[
                Permissions.InventoryPhysicalShipmentsEdit
            ]}>
                <button className="btn attention" onClick={acceptPartial}>
                    ACCEPT
                </button>
                <button className="btn cancel" onClick={cancelPartial}>
                    CANCEL
                </button>

            </HasPermission>;
        }
        else {
            return <HasPermission requiredPermissions={[
                Permissions.InventoryPhysicalShipmentsEdit
            ]}>
                <button className="btn attention" onClick={save} disabled={disableEdit()}>
                    SAVE
                </button>
            </HasPermission>;
        }
    }

    function goBack() {
        if (displayedShipment) {
            cancelPartial();
        }
        else {
            var reportId = getReportId();
            navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
        }
    }

    function save() {

        var reportId = getReportId();

        var payload = {
            reportId: reportId,
            orders: []
        } as ShipmentsUpdate;

        for (let shipment of shipments) {
            for (let order of shipment.orders) {

                var payloadOrder : any = {};
                payloadOrder.id = order.id;
                payloadOrder.serviceCenterId = shipment.serviceCenterId;
                payloadOrder.shipmentId = shipment.id;

                if (order.struts) {
                    payloadOrder.struts = true;
                }

                if (order.springs) {
                    payloadOrder.springs = true;
                }

                if (order.track) {
                    payloadOrder.track = true;
                }

                if (order.hardware) {
                    payloadOrder.hardware = true;
                }

                if (order.sections) {
                    payloadOrder.sections = true;
                }

                if (order.windows) {
                    payloadOrder.windows = true;
                }

                payload.orders.push(payloadOrder);
            }
        }

        service.updateShipments(reportId, payload)
            .then((result) => {
                if (result.status === StatusCode.Success) {
                    getPageData(reportId);
                }
            });
    }

    // shipments view

    async function getPageData(reportId: string) {
        if (reportId) {
            const result = await service.getShipmentData(reportId);
            if (result.status === StatusCode.Success) {
                setShipments(result.data.shipments);
                updatePageHeaders(result.data.shipments);
                setReportStatus(result.data.reportStatus);
                setTotalValue(result.data.totalValue);
            }
        }
        else {
            goBack();
        }
    }

    function updatePageHeaders(srcShipments: PhysicalInventoryShipmentInfo[]) {

        //total value
        let tempIncludedTrucks = 0;
        let tempIncludedContainers = 0;
        for (let shipment of srcShipments) {

            if (shipment.type == ShipmentType.Truck && isShipmentIncluded(shipment)) {
                tempIncludedTrucks++;
            }

            if (shipment.type == ShipmentType.Container && isShipmentIncluded(shipment)) {
                tempIncludedContainers++;
            }
        }
        setIncludedTrucks(tempIncludedTrucks);
        setIncludedContainers(tempIncludedContainers);
    }

    // orders view

    function selectShipment(idx: number) {

        var shipment = { ...shipments[idx] };
        var orders = shipment.orders.map((o: PhysicalInventoryShipmentOrderInfo) => { return { ...o }; });

        setDisplayedShipment(shipment);
        setDisplayedOrders(orders);
        updateShipmentHeaders(orders);
    }

    function acceptPartial() {

        if (displayedShipment) {
            const tmpShipments = [ ...shipments ];

            var tmpShipment = tmpShipments.find((s: PhysicalInventoryShipmentInfo) => s.id === displayedShipment.id);
            if (tmpShipment) {

                let oneChecked = {struts: false, springs: false, track: false, hardware: false, sections: false, windows:false };
                let oneUnchecked = { struts: false, springs: false, track: false, hardware: false, sections: false, windows: false };

                for (let order of displayedOrders) {
                    if (order.struts) {
                        oneChecked.struts = true;
                    }
                    else {
                        oneUnchecked.struts = true;
                    }

                    if (order.springs) {
                        oneChecked.springs = true;
                    }
                    else {
                        oneUnchecked.springs = true;
                    }

                    if (order.track) {
                        oneChecked.track = true;
                    }
                    else {
                        oneUnchecked.track = true;
                    }

                    if (order.hardware) {
                        oneChecked.hardware = true;
                    }
                    else {
                        oneUnchecked.hardware = true;
                    }

                    if (order.sections) {
                        oneChecked.sections = true;
                    }
                    else {
                        oneUnchecked.sections = true;
                    }

                    if (order.windows) {
                        oneChecked.windows = true;
                    }
                    else {
                        oneUnchecked.windows = true;
                    }
                }

                if(oneChecked.struts && oneUnchecked.struts) {
                    tmpShipment.struts = IncludeType.partial;
                }
                else if (oneChecked.struts) {
                    tmpShipment.struts = IncludeType.all;
                }
                else {
                    tmpShipment.struts = IncludeType.none;
                }

                if (oneChecked.springs && oneUnchecked.springs) {
                    tmpShipment.springs = IncludeType.partial;
                }
                else if (oneChecked.springs) {
                    tmpShipment.springs = IncludeType.all;
                }
                else {
                    tmpShipment.springs = IncludeType.none;
                }

                if (oneChecked.track && oneUnchecked.track) {
                    tmpShipment.track = IncludeType.partial;
                }
                else if (oneChecked.track) {
                    tmpShipment.track = IncludeType.all;
                }
                else {
                    tmpShipment.track = IncludeType.none;
                }

                if (oneChecked.hardware && oneUnchecked.hardware) {
                    tmpShipment.hardware = IncludeType.partial;
                }
                else if (oneChecked.hardware) {
                    tmpShipment.hardware = IncludeType.all;
                }
                else {
                    tmpShipment.hardware = IncludeType.none;
                }

                if (oneChecked.sections && oneUnchecked.sections) {
                    tmpShipment.sections = IncludeType.partial;
                }
                else if (oneChecked.sections) {
                    tmpShipment.sections = IncludeType.all;
                }
                else {
                    tmpShipment.sections = IncludeType.none;
                }

                if (oneChecked.windows && oneUnchecked.windows) {
                    tmpShipment.windows = IncludeType.partial;
                }
                else if (oneChecked.windows) {
                    tmpShipment.windows = IncludeType.all;
                }
                else {
                    tmpShipment.windows = IncludeType.none;
                }

                tmpShipment.orders = displayedOrders;
            }


            setDisplayedOrders([]);
            setShipments(tmpShipments);

            updatePageHeaders(tmpShipments);
            setDisplayedShipment(null);
        }
    }

    function cancelPartial() {
        setDisplayedShipment(null);
    }

    const updateShipmentHeaders = (tmpOrders: PhysicalInventoryShipmentOrderInfo[]) => {

        //let tempIncludedValue = 0;
        let tempIncludedOrderCount = 0;
        for (let order of tmpOrders) {
            if (isOrderIncluded(order)) {
                tempIncludedOrderCount++;
            }
        }

        setIncludedOrderCount(tempIncludedOrderCount);
    }

    function getIncludeClass(type: IncludeType) {
        switch (type) {
            case IncludeType.all:
                return 'include-all';
            case IncludeType.partial:
                return 'include-partial';
            case IncludeType.none:
                return 'include-none';
            default:
                return '';
        }
    }

    function orderStrutsClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.struts = !order.struts;
        setDisplayedOrders(tmpOrders);
    }

    function orderSpringsClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.springs = !order.springs;
        setDisplayedOrders(tmpOrders);
    }

    function orderTrackClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.track = !order.track;
        setDisplayedOrders(tmpOrders);
    }

    function orderHardwareClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.hardware = !order.hardware;
        setDisplayedOrders(tmpOrders);
    }

    function orderSectionsClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.sections = !order.sections;
        setDisplayedOrders(tmpOrders);
    }

    function orderWindowsClick(idx: number) {
        var tmpOrders = [...displayedOrders];
        const order = tmpOrders[idx];
        order.windows = !order.windows;
        setDisplayedOrders(tmpOrders);
    }

    function shipmentStrutsClick(idx: number) {
        const tmpShipments = [ ...shipments ];

        const shipment = tmpShipments[idx];
        shipment.struts = shipment.struts == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.struts = shipment.struts == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function shipmentSpringsClick(idx: number) {
        const tmpShipments = [...shipments];

        const shipment = tmpShipments[idx];
        shipment.springs = shipment.springs == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.springs = shipment.springs == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function shipmentTrackClick(idx: number) {
        const tmpShipments = [...shipments];

        const shipment = tmpShipments[idx];
        shipment.track = shipment.track == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.track = shipment.track == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function shipmentHardwareClick(idx: number) {
        const tmpShipments = [...shipments];

        const shipment = tmpShipments[idx];
        shipment.hardware = shipment.hardware == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.hardware = shipment.hardware == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function shipmentSectionsClick(idx: number) {
        const tmpShipments = [...shipments];

        const shipment = tmpShipments[idx];
        shipment.sections = shipment.sections == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.sections = shipment.sections == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function shipmentWindowsClick(idx: number) {
        const tmpShipments = [...shipments];

        const shipment = tmpShipments[idx];
        shipment.windows = shipment.windows == IncludeType.all ? IncludeType.none : IncludeType.all;
        for (let order of shipment.orders) {
            order.windows = shipment.windows == IncludeType.all;
        }

        setShipments(tmpShipments)
    }

    function isOrderIncluded(order: PhysicalInventoryShipmentOrderInfo): boolean {
        return order.struts || order.springs || order.track || order.hardware || order.sections || order.windows;
    }

    function isShipmentIncluded(shipment: PhysicalInventoryShipmentInfo): boolean {
        return shipment.struts != IncludeType.none
            || shipment.springs != IncludeType.none
            || shipment.track != IncludeType.none
            || shipment.hardware != IncludeType.none
            || shipment.sections != IncludeType.none
            || shipment.windows != IncludeType.none;
    }

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_SHIPMENTS} classes='inventory-physical'>
        {
            displayedShipment
                ? <PageBody>

                    <div className="row">
                        <div className="col">
                            {getButtons()}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-2">
                        </div>
                        <div className="col-4">
                            <span className="invnetory-label">SHIPMENT ID</span>
                            <span className="invnetory-value">{displayedShipment.id}</span>
                        </div>
                        <div className="col-4">
                            <span className="invnetory-label">INCLUDED ITEMS</span>
                            <span className="invnetory-value">{includedOrderCount}</span>
                        </div>
                        <div className="col-2">                            
                        </div>
                    </div>
                    <div className="row full-bleed">
                        <div className="col">
                            <div className="inventory-orders-legend">
                                <h4>Legend:</h4>
                                <h4 className="include-none">Included</h4>
                                <h4 className="include-all">Not Included</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">

                            <Table
                                header={[
                                    'LINE NUMBER',
                                    'ID',
                                    'DEALER NAME',
                                    'DESCRIPTION',
                                    'STRUTS',
                                    'SPRINGS',
                                    'TRACK',
                                    'HARDWARE',
                                    'SECTIONS',
                                    'WINDOWS'
                                ]}
                                noResultsMessage="No Shipments found">
                                {
                                    displayedOrders.map((o: PhysicalInventoryShipmentOrderInfo, idx: number) => {
                                        return <tr>
                                            <td><span>{o.lineNumber}</span></td>
                                            <td><span>{o.id}</span></td>
                                            <td><span>{o.dealerName}</span></td>
                                            <td><span>{o.description}</span></td>
                                            <td>{<div onClick={() => orderStrutsClick(idx)} className={"inventory-status " + (o.struts ? 'include-all' : 'include-none')} />}</td>
                                            <td>{<div onClick={() => orderSpringsClick(idx)} className={"inventory-status " + (o.springs ? 'include-all' : 'include-none')} />}</td>
                                            <td>{<div onClick={() => orderTrackClick(idx)} className={"inventory-status " + (o.track ? 'include-all' : 'include-none')} />}</td>
                                            <td>{<div onClick={() => orderHardwareClick(idx)} className={"inventory-status " + (o.hardware ? 'include-all' : 'include-none')} />}</td>
                                            <td>{<div onClick={() => orderSectionsClick(idx)} className={"inventory-status " + (o.sections ? 'include-all' : 'include-none')} />}</td>
                                            <td>{<div onClick={() => orderWindowsClick(idx)} className={"inventory-status " + (o.windows ? 'include-all' : 'include-none')} />}</td>
                                        </tr>
                                    })
                                }
                            </Table>
                        </div>
                    </div>
                </PageBody>
                : <PageBody>
                    <div className="row">
                        <div className="col">
                            {getButtons()}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-1">
                        </div>
                        <div className="col-3">
                            <span className="invnetory-label">INCLUDED TRUCKS</span>
                            <span className="invnetory-value">{includedTrucks}</span>
                        </div>
                        <div className="col-3">
                            <span className="invnetory-label">INCLUDED CONTAINERS</span>
                            <span className="invnetory-value">{includedContainers}</span>
                        </div>
                        <div className="col-3">
                            <span className="invnetory-label">TOTAL VALUE</span>
                            {
                                totalValue === undefined
                                    ? <span></span>
                                    : <span className="invnetory-value"> {formatCurrency(totalValue)} </span>
                            }
                        </div>
                        <div className="col-2">
                        </div>
                    </div>
                    <div className="row full-bleed">
                        <div className="col">
                            <div className="inventory-orders-legend">
                                <h4>Legend:</h4>
                                <h4 className="include-none">Include None</h4>
                                <h4 className="include-partial">Include Partial</h4>
                                <h4 className="include-all">Include All</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <Table
                                header={[
                                    'ID',
                                    'TYPE',
                                    'STATUS',
                                    'NAME',
                                    'STRUTS',
                                    'SPRINGS',
                                    'TRACK',
                                    'HARDWARE',
                                    'SECTIONS',
                                    'WINDOWS'
                                ]}
                                noResultsMessage="No Shipments found">
                                {
                                    shipments.map((s: PhysicalInventoryShipmentInfo, idx: number) => {
                                        return <tr>
                                            <td>
                                                <button
                                                    id="id"
                                                    onClick={() => selectShipment(idx)}
                                                    type="button"
                                                    className="select-partial-button" >
                                                    {s.id}
                                                </button>
                                            </td>
                                            <td><span>{ShipmentTypeMetadata[s.type].display}</span></td>
                                            <td>
                                                <span className={"text-uppercase shipment-status-color " + ShipmentStatusMetadata[s.status].class}>
                                                    {ShipmentStatusMetadata[s.status].display}
                                                </span>
                                            </td>
                                            <td><span>{s.name}</span></td>
                                            <td>{<div onClick={() => shipmentStrutsClick(idx)} className={"inventory-status " + getIncludeClass(s.struts)} />}</td>
                                            <td>{<div onClick={() => shipmentSpringsClick(idx)} className={"inventory-status " + getIncludeClass(s.springs)} />}</td>
                                            <td>{<div onClick={() => shipmentTrackClick(idx)} className={"inventory-status " + getIncludeClass(s.track)} />}</td>
                                            <td>{<div onClick={() => shipmentHardwareClick(idx)} className={"inventory-status " + getIncludeClass(s.hardware)} />}</td>
                                            <td>{<div onClick={() => shipmentSectionsClick(idx)} className={"inventory-status " + getIncludeClass(s.sections)} />}</td>
                                            <td>{<div onClick={() => shipmentWindowsClick(idx)} className={"inventory-status " + getIncludeClass(s.windows)} />}</td>
                                        </tr>
                                    })
                                }
                            </Table>
                        </div>
                    </div>
                </PageBody>
        }
    </PageContent >
});